import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth.js';
import { AppShellV2 } from './app/AppShell.js';
import { CompaniesView } from './views/companies/CompaniesView.js';
import { ListDetailView } from './views/lists/ListDetailView.js';
import { ListsIndex } from './views/lists/ListsIndex.js';
import { PeopleView } from './views/people/PeopleView.js';
import { AuditLogPage } from './pages/AuditLogPage.js';
import { ColleaguesPage } from './pages/ColleaguesPage.js';
import { ContactNewPage } from './pages/ContactNewPage.js';
import { ContactDetailPage } from './pages/ContactDetailPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { TrashPage } from './pages/TrashPage.js';

export function App() {
  const { state } = useAuth();

  if (state.kind === 'loading') {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-muted">Loading…</p>
      </main>
    );
  }

  if (state.kind === 'helper-done') {
    return <HelperDone />;
  }

  if (state.kind === 'unauthenticated') {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  // /contacts and /contacts/:id are kept as backwards-compat redirects
  // so any pre-v2 deep links still resolve. New traffic uses
  // /people, /companies, /lists.
  return (
    <Routes>
      <Route element={<AppShellV2 />}>
        <Route path="/" element={<Navigate to="/people" replace />} />
        <Route path="/contacts" element={<Navigate to="/people" replace />} />
        <Route path="/contacts/new" element={<ContactNewPage />} />
        <Route path="/contacts/:id" element={<ContactDetailPage />} />
        <Route path="/companies" element={<CompaniesView />} />
        <Route path="/companies/:id" element={<CompaniesView />} />
        <Route path="/people" element={<PeopleView />} />
        <Route path="/people/:id" element={<PeopleView />} />
        <Route path="/colleagues" element={<ColleaguesPage />} />
        <Route path="/dial" element={<ListsIndex />} />
        <Route path="/dial/:id" element={<ListDetailView />} />
        <Route path="/trash" element={<TrashPage />} />
        <Route path="/audit" element={<AuditLogPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/people" replace />} />
      </Route>
    </Routes>
  );
}

// Rendered in the helper tab opened from the side-panel iframe after
// MSAL completes its redirect. The iframe has already been told via
// BroadcastChannel to refresh into its authenticated state — this page
// just gives the user something tidy to look at while we try to close
// the tab. Auto-close is best-effort; some Chrome versions block
// script-driven close, hence the manual button.
function HelperDone() {
  useEffect(() => {
    const id = window.setTimeout(() => window.close(), 200);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-sm space-y-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-strong">You're signed in</h1>
        <p className="text-sm text-mid">
          You can close this tab. Cloud Contacts is ready in the side panel.
        </p>
        <button
          type="button"
          onClick={() => window.close()}
          className="btn-primary px-4 py-2 text-sm font-medium"
        >
          Close tab
        </button>
      </div>
    </main>
  );
}
